import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import { useIonToast, isPlatform } from "@ionic/react";
import { lazy, Suspense } from "react";
import {
  IconBrain,
  IconUserScan,
  IconMoodSmile,
  IconShieldLock,
} from "@tabler/icons-react";

import AiApi from "../../../../api/ai";
import SkeletonText from "../../../SkeletonText";

import styles from "../../free-text/styles.module.css";

const TextEditor = lazy(() => import("../../../common/text-editor"));

const isMobile = isPlatform("android") || isPlatform("ios");

export default function Instructions() {
  const queryClient = useQueryClient();
  const intl = useIntl();
  const [present] = useIonToast();

  const {
    data: aiData,
    isLoading: isGettingAi,
    isRefetching: isRefetchingAi,
  } = useQuery({
    queryFn: () => AiApi.getAiSettings(),
    queryKey: ["getAiSettings"],
    refetchOnWindowFocus: false,
  });

  const mutateUpdateSystemPrompt = useMutation({
    // @ts-ignore
    mutationFn: AiApi.putAiSettingsByFieldV2,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getAiSettings"],
      });
      return present({
        message: intl.formatMessage({ id: "aiUpdateSucces" }),
        duration: 4000,
        color: "light",
        position: "top",
        cssClass: "success-toast",
      });
    },
    onError: () => {
      return present({
        message: intl.formatMessage({ id: "common.tryItLater" }),
        duration: 5000,
        color: "light",
        position: "top",
        cssClass: "error-toast",
      });
    },
  });

  const isRequesting =
    isGettingAi || isRefetchingAi || mutateUpdateSystemPrompt.isPending;
  const systemPrompt = aiData?.data?.aiSettings?.systemPrompt || null;

  return isRequesting ? (
    <div className="flex flex-column gap-2">
      <SkeletonText rows={12} />
    </div>
  ) : (
    <Suspense fallback={<SkeletonText rows={10} />}>
      <div className="flex w-100 h-100 gap-3">
        <div className={`h-100 ${!isMobile ? "w-75" : "w-100"}`}>
          <TextEditor
            text={systemPrompt}
            onSave={(newPrompt) =>
              mutateUpdateSystemPrompt.mutate({
                field: "systemPrompt",
                value: newPrompt,
                embeddingId: undefined,
              })
            }
            searchBar={false}
            maxLength={10000}
          />
        </div>
        {!isMobile && (
          <aside className="h-100" style={{ width: "25%", minWidth: "300px" }}>
            <div className={styles.helpCard}>
              <div className={styles.helpHeader}>
                <IconBrain size={20} className={styles.helpIcon} />
                <h3>
                  <FormattedMessage id="ai.instructionsHelp.title" />
                </h3>
              </div>

              <div className={styles.helpBody}>
                <p className={styles.introText}>
                  <FormattedMessage id="ai.instructionsHelp.intro" />
                </p>

                <ul className={styles.helpList}>
                  <li>
                    <div className={styles.listIconBox}>
                      <IconUserScan size={16} />
                    </div>
                    <div>
                      <strong>
                        <FormattedMessage id="ai.instructionsHelp.role.title" />
                      </strong>
                      <span>
                        <FormattedMessage id="ai.instructionsHelp.role.desc" />
                      </span>
                    </div>
                  </li>

                  <li>
                    <div className={styles.listIconBox}>
                      <IconMoodSmile size={16} />
                    </div>
                    <div>
                      <strong>
                        <FormattedMessage id="ai.instructionsHelp.tone.title" />
                      </strong>
                      <span>
                        <FormattedMessage id="ai.instructionsHelp.tone.desc" />
                      </span>
                    </div>
                  </li>

                  <li>
                    <div className={styles.listIconBox}>
                      <IconShieldLock size={16} />
                    </div>
                    <div>
                      <strong>
                        <FormattedMessage id="ai.instructionsHelp.rules.title" />
                      </strong>
                      <span>
                        <FormattedMessage id="ai.instructionsHelp.rules.desc" />
                      </span>
                    </div>
                  </li>
                </ul>

                <div className={styles.proTip}>
                  <FormattedMessage id="ai.instructionsHelp.tip" />
                </div>
              </div>
            </div>
          </aside>
        )}
      </div>
    </Suspense>
  );
}
